import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-wine-950">
      <Header />

      <div className="pt-24 pb-16 bg-cream-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-wine-600 hover:text-wine-700 mb-6 text-sm font-medium transition-colors">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Home
          </Link>

          <h1 className="text-4xl font-bold text-wine-950 mb-6">Privacy Policy</h1>

          <div className="prose max-w-none space-y-6 text-stone-600">
            <p className="text-lg">
              At <strong className="text-wine-950">Trademark Text Logo</strong>, we are committed to protecting your privacy and ensuring that your personal information is handled in a safe and responsible manner. This Privacy Policy outlines how we collect, use, and protect your personal data, and it applies to all interactions with our website, products, and services.
            </p>

            <section>
              <h2 className="text-2xl font-bold text-wine-950 mt-8 mb-4">Information We Collect</h2>
              <p>We collect personal information from you when you visit our website, register for an account, make a purchase, or communicate with us. The types of information we may collect include:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-wine-950">Personal Identification Information:</strong> Name, email address, phone number, billing and shipping addresses, and payment information.</li>
                <li><strong className="text-wine-950">Non-personal Identification Information:</strong> IP address, browser type, operating system, and website usage information.</li>
                <li><strong className="text-wine-950">Cookies and Tracking Technologies:</strong> We use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, and personalize content.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-wine-950 mt-8 mb-4">How We Use Your Information</h2>
              <p>We use the information we collect for various purposes, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>To process and manage your transactions and orders.</li>
                <li>To communicate with you about your orders, services, or updates.</li>
                <li>To respond to your inquiries and provide customer support.</li>
                <li>To improve our website, products, and services.</li>
                <li>To send promotional materials, newsletters, and other marketing communications (if you have opted-in to receive them).</li>
                <li>To prevent fraud and ensure the security of our website.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-wine-950 mt-8 mb-4">How We Protect Your Information</h2>
              <p>We implement a variety of security measures to maintain the safety of your personal information, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-wine-950">Encryption:</strong> We use SSL encryption technology to protect sensitive data during transmission.</li>
                <li><strong className="text-wine-950">Secure Storage:</strong> Your personal information is stored in a secure environment with restricted access.</li>
                <li><strong className="text-wine-950">Regular Audits:</strong> We regularly review our security practices to ensure that your data is safeguarded.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-wine-950 mt-8 mb-4">Sharing Your Information</h2>
              <p>We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following cases:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-wine-950">Service Providers:</strong> We may share your data with trusted third-party service providers who assist us in operating our website, processing payments, or providing customer service.</li>
                <li><strong className="text-wine-950">Legal Compliance:</strong> We may disclose your information to comply with legal obligations, enforce our site policies, or protect our rights or the rights of others.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-wine-950 mt-8 mb-4">Your Rights and Choices</h2>
              <p>You have the following rights regarding your personal information:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-wine-950">Access and Correction:</strong> You may request access to your personal information and request corrections or updates to your data.</li>
                <li><strong className="text-wine-950">Opt-Out:</strong> If you no longer wish to receive marketing communications from us, you can opt-out by clicking the unsubscribe link in any email or by contacting us directly.</li>
                <li><strong className="text-wine-950">Deletion:</strong> You may request that we delete your personal information, subject to applicable legal and regulatory obligations.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-wine-950 mt-8 mb-4">Cookies and Tracking Technologies</h2>
              <p>We use cookies and similar technologies to enhance your experience on our website. You can choose to disable cookies through your browser settings, but this may affect your ability to access certain features of our website.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-wine-950 mt-8 mb-4">Data Retention</h2>
              <p>We retain your personal information for as long as necessary to fulfill the purposes for which it was collected, including legal, accounting, or reporting requirements.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-wine-950 mt-8 mb-4">Third-Party Links</h2>
              <p>Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites, and we encourage you to review their privacy policies before providing any personal information.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-wine-950 mt-8 mb-4">Children's Privacy</h2>
              <p>Our website is not intended for children under the age of 13. We do not knowingly collect or solicit personal information from children.</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-wine-950 mt-8 mb-4">Changes to This Privacy Policy</h2>
              <p>We reserve the right to modify or update this Privacy Policy at any time. Any changes will be posted on this page with an updated "Effective Date."</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-wine-950 mt-8 mb-4">Contact Us</h2>
              <p>If you have any questions or concerns about this Privacy Policy, please contact us at:</p>
              <div className="bg-white p-6 rounded-xl border border-stone-200 mt-4 text-stone-700">
                <p><strong className="text-wine-950">Company:</strong> Trademark Text Logo</p>
                <p><strong className="text-wine-950">Address:</strong> 18952 MacArthur Blvd Suite 100, Irvine, CA 92612, United States</p>
                <p><strong className="text-wine-950">Phone:</strong> (800) 555-1234</p>
                <p><strong className="text-wine-950">Email:</strong> info@trademarktextlogo.com</p>
              </div>
            </section>

            <p className="text-sm text-stone-400 mt-8 pt-8 border-t border-stone-200">
              <strong>Effective Date:</strong> February 1, 2026
            </p>

            <p className="mt-6">By using our website and services, you consent to the terms of this Privacy Policy.</p>
          </div>
        </div>
      </div>

      <Footer onOpenContactForm={() => {}} />
    </div>
  );
};

export default PrivacyPolicy;
